import React from "react";
import { GhlConversationData } from "../types";

interface ConversationCardProps {
  data: GhlConversationData;
}

const formatDate = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const headerIconStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-secondary) 20%, transparent)",
};

const getBadgeStyle = (type: string): React.CSSProperties => {
  if (type.toLowerCase() === 'sms') {
    return {
      background: "color-mix(in srgb, var(--color-accent-warm) 15%, transparent)",
      color: "color-mix(in srgb, var(--color-accent-warm) 60%, var(--color-text-primary) 40%)",
    };
  }
  return {
    background: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
    color: "var(--color-primary)",
  };
};

export const ConversationCard: React.FC<ConversationCardProps> = ({ data }) => {
  const { messages, contact_name } = data;

  return (
    <div className="my-1 w-full max-w-full overflow-hidden rounded-2xl border border-theme-border bg-theme-surface">
      <div className="flex items-center justify-between border-b border-theme-border px-4 py-3 bg-theme-background/30">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl" style={headerIconStyle}>
            <span className="material-icons-outlined text-[18px] text-theme-primary">chat</span>
          </span>
          <div>
            <p className="text-[13px] font-bold text-theme-fg">
              {contact_name || "Conversation"}
            </p>
            <p className="text-[11px] text-theme-fg-muted mt-0.5">
              {messages.length} {messages.length === 1 ? "Message" : "Messages"}
            </p>
          </div>
        </div>
      </div>

      <div className="flex max-h-[400px] flex-col-reverse gap-3 overflow-y-auto p-4">
        {messages.length === 0 && (
          <p className="py-6 text-center text-[12.5px] text-theme-fg-muted">
            No messages in this conversation.
          </p>
        )}
        {/* Messages are sorted newest first, so with flex-col-reverse they render oldest top to newest bottom */}
        {messages.map((m, i) => {
          const isOutbound = m.direction === 'outbound';
          return (
            <div
              key={i}
              className={`flex w-full ${isOutbound ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`flex max-w-[85%] flex-col gap-1 rounded-2xl px-3.5 py-2.5 ${
                  isOutbound
                    ? 'rounded-tr-sm bg-theme-primary text-white'
                    : 'rounded-tl-sm border border-theme-border bg-theme-background text-theme-fg'
                }`}
              >
                <div className="flex items-center gap-2 justify-between">
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wide rounded-sm px-1.5 py-0.5 ${
                      isOutbound ? 'bg-white/20' : ''
                    }`}
                    style={isOutbound ? undefined : getBadgeStyle(m.type)}
                  >
                    {m.type}
                  </span>
                </div>
                <div className={`whitespace-pre-wrap text-[13px] leading-relaxed ${
                  isOutbound ? 'text-white/95' : 'text-theme-fg/90'
                }`}>
                  {m.body}
                </div>
                <div className={`text-[10.5px] text-right mt-1 ${
                  isOutbound ? 'text-white/70' : 'text-theme-fg-muted'
                }`}>
                  {formatDate(m.date_sent)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
